// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type ComponentType } from 'react';
import { styled, css } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { isBaseLevel } from '../../../../model/hierarchy';
import { ITEM_HEIGHT_BASE_LEVEL } from '../../../../model/table';
import { DEFAULT_LEVEL } from '../../common/constants';
import { getRowStyle } from '../../common/context/roadmap-style-marshal';
import RoadmapStyleMarshalContext, {
	type RoadmapStyleMarshalProps,
} from '../../common/context/roadmap-style-marshal/context/index.tsx';
import type { PercentPosition } from '../../common/utils/timeline';
import type { APIProps as BarProps } from '../bar/view.tsx';

type State = {
	isBarHovered: boolean;
};

type APIProps = {
	isSelectEnabled: boolean;
	isHoverEnabled: boolean;
	getPositionFromDate: (date: Date) => PercentPosition;
	id: IssueId;
	parentId: IssueId | undefined;
	level: number;
	Bar: ComponentType<BarProps>;
	itemHeight: number;
	onClick: (id: IssueId) => void;
};

type Props = APIProps & {
	timelineWidth: number;
};

// eslint-disable-next-line jira/react/no-class-components
class ChartItem extends PureComponent<Props, State> {
	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static whyDidYouRender = true;

	static defaultProps = {
		level: DEFAULT_LEVEL,
	};

	static contextType = RoadmapStyleMarshalContext;

	state = {
		isBarHovered: false,
	};

	onSetBarRef = (elem: HTMLElement | null) => {
		if (elem) {
			this.barRef = elem;
		}
	};

	onBarClick = () => {
		const { id, onClick, isSelectEnabled } = this.props;

		onClick(id);

		if (isSelectEnabled) {
			this.context.onRowSelect(id);
		}
	};

	onBarMouseEnter = () => {
		const { isHoverEnabled } = this.props;
		if (isHoverEnabled) {
			this.setState({ isBarHovered: true });
		}
	};

	onBarMouseLeave = () => {
		const { isHoverEnabled } = this.props;
		if (isHoverEnabled) {
			this.setState({ isBarHovered: false });
		}
	};

	onRowMouseEnter = () => {
		const { id } = this.props;
		const { onRowMouseEnter } = this.context;

		onRowMouseEnter(id);
	};

	onRowMouseLeave = () => {
		const { id } = this.props;
		const { onRowMouseLeave } = this.context;

		onRowMouseLeave(id);
	};

	// @ts-expect-error - TS2564 - Property 'context' has no initializer and is not definitely assigned in the constructor.
	context: RoadmapStyleMarshalProps;

	// @ts-expect-error - TS2564 - Property 'barRef' has no initializer and is not definitely assigned in the constructor.
	barRef: HTMLElement | null;

	// @ts-expect-error - TS2564 - Property 'containerRef' has no initializer and is not definitely assigned in the constructor.
	containerRef: HTMLElement | null;

	renderBar() {
		const { id, isSelectEnabled, getPositionFromDate, Bar } = this.props;
		const { isBarHovered } = this.state;

		return (
			<Bar
				id={id}
				innerRef={this.onSetBarRef}
				isSelectEnabled={isSelectEnabled}
				isHovered={isBarHovered}
				isDone={false}
				onClick={this.onBarClick}
				onMouseEnter={this.onBarMouseEnter}
				onMouseLeave={this.onBarMouseLeave}
				getPositionFromDate={getPositionFromDate}
			/>
		);
	}

	render() {
		const { id, parentId, level, itemHeight } = this.props;

		return (
			<Container
				level={level}
				itemHeight={isBaseLevel(level) ? ITEM_HEIGHT_BASE_LEVEL : itemHeight}
				onMouseEnter={this.onRowMouseEnter}
				onMouseLeave={this.onRowMouseLeave}
				{...getRowStyle(id, parentId)}
			>
				{this.renderBar()}
			</Container>
		);
	}
}

interface ContainerProps {
	level: number;
}

export interface BaseItemStylesProps {
	itemHeight: number;
}

// RLSS-2812 replace the first color.background.input to color.background.accent.gray.subtlest
//           replace the second color.background.neutral to color.background.accent.gray.subtler
//           Consider finding even a better token than the suggested ones here.
const baseItemStyles = css<BaseItemStylesProps>({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: `${({ itemHeight }: BaseItemStylesProps) => itemHeight}px`,
	transition: 'background-color 100ms linear',
	'-RowBackgroundColor':
		'var( --item-selected-color, var(--item-hover-color, var(--base-background-color)) )',
	backgroundColor: 'var(--row-background-color)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	'-BaseBackgroundColor': token('color.background.input', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:nth-of-type(odd)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		'-BaseBackgroundColor': token('color.background.input.hovered', colors.N10),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Container = styled.div<ContainerProps & BaseItemStylesProps>(baseItemStyles, {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	overflow: ({ level }) => (isBaseLevel(level) ? 'visible' : 'hidden'),
});

export default ChartItem;

export type { APIProps };

interface ContainerProps {
	level: number;
}
